import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from '../ui/Logo';
import { getTourProgress } from './TourSystem';

interface WelcomeModalProps {
    onStartTour: (step?: number) => void;
}

const WelcomeModal: React.FC<WelcomeModalProps> = ({ onStartTour }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [resumeStep, setResumeStep] = useState<number | null>(null);
    const [isRetake, setIsRetake] = useState(false);
    const hasCheckedInitial = useRef(false);

    // Effect for handling manual trigger (retake)
    useEffect(() => {
        const handleRetake = () => {
            setIsRetake(true);
            setResumeStep(null);
            setIsOpen(true);
        };

        window.addEventListener('techunity-retake-tour', handleRetake);
        return () => window.removeEventListener('techunity-retake-tour', handleRetake);
    }, []);

    // Effect for initial load check
    useEffect(() => {
        if (hasCheckedInitial.current) return;
        hasCheckedInitial.current = true;

        // Check if we are auto-starting a tour from a deep dive
        // Handle HashRouter: params are after the #
        const hash = window.location.hash;
        const queryStart = hash.indexOf('?');
        const queryString = queryStart >= 0 ? hash.substring(queryStart + 1) : '';
        const searchParams = new URLSearchParams(queryString);
        const autoStartTour = searchParams.get('autoStartTour');

        if (autoStartTour === 'true') {
            // Do not show modal if we are auto-starting a tour
            return;
        }

        const hasSeen = localStorage.getItem('hasSeenWelcomeModal');
        const savedStep = getTourProgress('landing');

        if (!hasSeen) {
            const timer = setTimeout(() => setIsOpen(true), 1500);
            return () => clearTimeout(timer);
        } else if (savedStep !== null) {
            // User has seen welcome but has unfinished tour
            setResumeStep(savedStep);
            const timer = setTimeout(() => setIsOpen(true), 1500);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleStart = () => {
        setIsOpen(false);
        localStorage.setItem('hasSeenWelcomeModal', 'true');
        onStartTour(resumeStep ?? 0);
    };

    const handleSkip = () => {
        setIsOpen(false);
        localStorage.setItem('hasSeenWelcomeModal', 'true');
    };

    const isResuming = resumeStep !== null;
    // const isRetake is already defined in state.

    // Dynamic Text Logic
    let title = 'Welcome';
    let description = (
        <span>A virtual company designed to <span className="text-premium-gold font-normal">reduce unemployment</span>. We connect global talent, manage projects, and build the future of work.</span>
    );
    let startBtnText = 'Yes, Start Tour';
    let cancelBtnText = 'No, thanks';

    if (isResuming) {
        title = 'Welcome Back';
        description = <span>You have an unfinished tour. Would you like to <span className="text-premium-gold font-normal">continue where you left off</span>?</span>;
        startBtnText = 'Yes, Continue';
        cancelBtnText = 'No, Thanks';
    } else if (isRetake) {
        title = 'Retake Tour?';
        description = <span>Would you like to start the guided tour from the beginning?</span>;
        startBtnText = 'Yes, Start';
        cancelBtnText = 'Cancel';
    }

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        transition={{ type: "spring", duration: 0.6 }}
                        className="bg-premium-wood-dark border border-premium-gold/30 rounded-2xl max-w-sm w-full overflow-hidden shadow-[0_0_50px_rgba(212,175,55,0.2)] relative"
                    >
                        {/* Gold Top Border */}
                        <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-premium-wood-dark via-premium-gold to-premium-wood-dark"></div>

                        <div className="p-5 text-center relative z-10">
                            {/* Logo - Reduced size */}
                            <div className="mb-3 flex justify-center">
                                <Logo variant="header" size="sm" />
                            </div>

                            {/* Title */}
                            <h2 className="text-xl font-serif font-bold text-transparent bg-clip-text bg-gradient-to-b from-premium-gold-light via-premium-gold to-premium-gold-dark mb-2 drop-shadow-md">
                                {title}
                            </h2>

                            {/* Description */}
                            <p className="text-white/80 text-xs leading-relaxed mb-4 font-light">
                                {description}
                            </p>

                            {/* Tutorial Offer - Compact box (Only show for fresh welcome, not retake/resume to decrease noise) */}
                            {(!isResuming && !isRetake) && (
                                <div className="bg-white/5 border border-white/10 rounded-lg p-3 mb-4 backdrop-blur-sm">
                                    <p className="text-premium-gold font-bold mb-0.5 text-sm">🚀 Want a quick tour?</p>
                                    <p className="text-white/60 text-[10px] leading-tight">
                                        We can guide you through the login and sign up process.
                                    </p>
                                </div>
                            )}

                            {/* Buttons */}
                            <div className="flex flex-col gap-2 sm:flex-row">
                                <button
                                    onClick={handleStart}
                                    className="flex-1 py-2 bg-gradient-to-r from-premium-gold-dark to-premium-gold-light text-premium-wood-dark font-bold rounded-lg shadow-lg hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all transform hover:scale-[1.02] active:scale-95 text-xs uppercase tracking-wider"
                                >
                                    {startBtnText}
                                </button>
                                <button
                                    onClick={handleSkip}
                                    className="flex-1 py-2 border border-white/10 hover:border-white/30 text-white/50 hover:text-white rounded-lg transition-all text-xs font-medium uppercase tracking-wider hover:bg-white/5"
                                >
                                    {cancelBtnText}
                                </button>
                            </div>
                        </div>

                        <div className="absolute inset-0 z-0 opacity-10 pointer-events-none mix-blend-overlay" style={{ backgroundImage: `url('https://www.transparenttextures.com/patterns/wood-pattern.png')` }}></div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default WelcomeModal;
